/**
 * navigation/shellNav.tsx
 * In-shell router context for the authenticated console. Faithful port of the
 * design's navigate()/goApp() helpers (app.jsx). The shell keeps a single
 * { area, screen } route in state; screens call useShellNav() to move around
 * while the TopBar + SubNav stay mounted.
 */

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ShellArea, ShellRoute, ShellScreen, TenantScreen } from './types';
import type { IconName } from '../components/icon';
import type { Role } from '../data/platformSeed';

/** Tenant settings nav (role-gated) — ported from shell.jsx NAV. */
export const NAV: { id: TenantScreen; label: string; icon: IconName; roles: Role[] }[] = [
  { id: 'org', label: 'Organization', icon: 'home', roles: ['ADMIN', 'EDITOR', 'VIEWER'] },
  { id: 'team', label: 'Team & Roles', icon: 'users', roles: ['ADMIN'] },
  { id: 'plan', label: 'Plan & Usage', icon: 'star', roles: ['ADMIN', 'EDITOR'] },
  { id: 'billing', label: 'Billing', icon: 'download', roles: ['ADMIN'] },
  { id: 'security', label: 'Security', icon: 'lock', roles: ['ADMIN', 'EDITOR', 'VIEWER'] },
  { id: 'audit', label: 'Audit Log', icon: 'filter', roles: ['ADMIN'] },
  { id: 'privacy', label: 'Data & Privacy', icon: 'eye', roles: ['ADMIN'] },
  // UICC-006 N-1. Placed away from 'plan' and 'billing' in the list as well as
  // on the screen: the seats/customers confusion is easier to prevent than to
  // explain, and adjacency in a nav is its own kind of adjacency.
  { id: 'customer-access', label: 'Customer access', icon: 'users', roles: ['ADMIN'] },
  { id: 'connections', label: 'Connections', icon: 'link', roles: ['ADMIN', 'EDITOR'] },
  { id: 'hq', label: 'HQ Console', icon: 'store', roles: ['ADMIN'] },
];

/** Platform Admin Console nav — ported from admin.jsx ADMIN_NAV. */
export const ADMIN_NAV: { id: string; label: string; icon: IconName }[] = [
  { id: 'admin-overview', label: 'Overview', icon: 'home' },
  { id: 'admin-applications', label: 'Applications', icon: 'grid' },
  { id: 'admin-catalog', label: 'Plan catalog', icon: 'star' },
  { id: 'admin-organizations', label: 'Organizations', icon: 'users' },
  { id: 'admin-billing', label: 'Adjustments & statements', icon: 'rupee' },
  // UICC CH-U5 — investigating stuck events. Last in the list because it is a
  // place you are SENT by an alert, not somewhere you browse to.
  { id: 'admin-delivery-failures', label: 'Delivery failures', icon: 'warn' },
  // UICC-006 N-2 … N-6. Incident and reference surfaces, ordered as DB-006 §5
  // prioritises them: the two you are SENT to by an alert first, then the
  // read-mostly registers.
  { id: 'admin-realm-mismatch', label: 'Realm mismatches', icon: 'warn' },
  { id: 'admin-signing-keys', label: 'Signing keys', icon: 'lock' },
  { id: 'admin-bypass-ledger', label: 'RLS bypass ledger', icon: 'filter' },
  { id: 'admin-customer-activity', label: 'Customer activity', icon: 'eye' },
  { id: 'admin-audiences', label: 'Audience registry', icon: 'grid' },
];

interface ShellNavValue {
  route: ShellRoute;
  /** Navigate within tenant settings (area = tenant), e.g. navigate('billing'). */
  navigate: (screen: ShellScreen) => void;
  /** Jump to any area + screen, e.g. goApp('admin', 'admin-overview'). */
  goApp: (area: ShellArea, screen: ShellScreen) => void;
  /** Sign out — provided by the root navigator. */
  signOut: () => void;
}

const ShellNavContext = createContext<ShellNavValue | null>(null);

export function ShellNavProvider({
  signOut,
  initial = { area: 'tenant', screen: 'home' },
  children,
}: {
  signOut: () => void;
  initial?: ShellRoute;
  children: React.ReactNode;
}) {
  const [route, setRoute] = useState<ShellRoute>(initial);

  const navigate = useCallback((screen: ShellScreen) => {
    setRoute({ area: 'tenant', screen });
  }, []);
  const goApp = useCallback((area: ShellArea, screen: ShellScreen) => {
    setRoute({ area, screen });
  }, []);

  const value = useMemo<ShellNavValue>(
    () => ({ route, navigate, goApp, signOut }),
    [route, navigate, goApp, signOut],
  );

  return <ShellNavContext.Provider value={value}>{children}</ShellNavContext.Provider>;
}

export function useShellNav(): ShellNavValue {
  const ctx = useContext(ShellNavContext);
  if (!ctx) throw new Error('useShellNav must be used within ShellNavProvider');
  return ctx;
}
